import jsPDF from 'jspdf';
import { LTSSService, StaffMetrics, Alert, ClientUtilization, FinancialSummary } from '../types/ltss';

interface AuditReportData {
  fileName: string;
  services: LTSSService[];
  alerts: Alert[];
  staffMetrics: StaffMetrics[];
  clientUtilization: ClientUtilization[];
  financials: FinancialSummary;
}

// Generate audit report and return as Blob for Google Drive upload
export const generateAuditReportBlob = async (data: AuditReportData): Promise<Blob> => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  let y = 20;
  let pageNum = 1;

  // Helper functions
  const newPage = () => {
    addFooter();
    pdf.addPage();
    pageNum++;
    y = 25;
    addPageHeader();
  };

  const addPageHeader = () => {
    pdf.setFillColor(30, 41, 59);
    pdf.rect(0, 0, pageWidth, 15, 'F');
    pdf.setFontSize(8);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(200, 200, 200);
    pdf.text('LTSS Analytics - Compliance & Operations Report', margin, 10);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 60, 10);
    pdf.setTextColor(0, 0, 0);
  };

  const addFooter = () => {
    pdf.setFontSize(8);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Page ${pageNum}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    pdf.text('Confidential - For Internal Use Only', margin, pageHeight - 10);
    pdf.setTextColor(0, 0, 0);
  };

  const checkPageBreak = (neededSpace: number) => {
    if (y + neededSpace > pageHeight - 25) {
      newPage();
    }
  };

  // Title page
  pdf.setFillColor(30, 41, 59);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Logo area
  pdf.setFillColor(59, 130, 246);
  pdf.roundedRect(pageWidth / 2 - 25, 40, 50, 50, 5, 5, 'F');
  pdf.setFontSize(24);
  pdf.setFont('times', 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('LTSS', pageWidth / 2, 65, { align: 'center' });
  pdf.setFontSize(10);
  pdf.text('Analytics', pageWidth / 2, 78, { align: 'center' });

  // Title
  pdf.setFontSize(28);
  pdf.setFont('times', 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('Compliance & Operations', pageWidth / 2, 115, { align: 'center' });
  pdf.text('Audit Report', pageWidth / 2, 128, { align: 'center' });

  // Report info
  pdf.setFontSize(12);
  pdf.setFont('times', 'normal');
  pdf.setTextColor(200, 200, 200);
  pdf.text(`Report Date: ${new Date().toLocaleDateString()}`, pageWidth / 2, 155, { align: 'center' });
  pdf.text(`Source: ${data.fileName}`, pageWidth / 2, 165, { align: 'center' });
  pdf.text(`Total Services Analyzed: ${data.services.length.toLocaleString()}`, pageWidth / 2, 175, { align: 'center' });

  // Footer on title page
  pdf.setFontSize(10);
  pdf.setTextColor(128, 128, 128);
  pdf.text('Venus Health Care Solutions', pageWidth / 2, pageHeight - 30, { align: 'center' });
  pdf.text('www.ltssanalytics.com', pageWidth / 2, pageHeight - 22, { align: 'center' });

  // Start content pages
  newPage();

  // Executive Summary
  pdf.setFontSize(16);
  pdf.setFont('times', 'bold');
  pdf.setTextColor(30, 41, 59);
  pdf.text('Executive Summary', margin, y);
  y += 10;

  pdf.setFontSize(10);
  pdf.setFont('times', 'normal');
  pdf.setTextColor(60, 60, 60);

  const summaryText = `This report provides a comprehensive analysis of ${data.services.length.toLocaleString()} LTSS services. ` +
    `The analysis identified ${data.alerts.length} compliance alerts requiring attention. ` +
    `Total revenue analyzed: $${data.financials.totalAmountPaid.toLocaleString()}.`;
  
  const summaryLines = pdf.splitTextToSize(summaryText, contentWidth);
  pdf.text(summaryLines, margin, y);
  y += summaryLines.length * 5 + 10;

  // Key Metrics
  checkPageBreak(40);
  pdf.setFontSize(14);
  pdf.setFont('times', 'bold');
  pdf.setTextColor(30, 41, 59);
  pdf.text('Key Metrics', margin, y);
  y += 8;

  const metrics = [
    ['Total Services', data.services.length.toLocaleString()],
    ['Active Staff', data.staffMetrics.length.toString()],
    ['Active Clients', data.clientUtilization.length.toString()],
    ['Total Revenue', `$${data.financials.totalAmountPaid.toLocaleString()}`],
    ['Claims Paid', data.financials.claimsPaid.toString()],
    ['Claims Pending', data.financials.claimsPending.toString()],
    ['Total Alerts', data.alerts.length.toString()],
  ];

  metrics.forEach(([label, value]) => {
    checkPageBreak(6);
    pdf.setFontSize(10);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(60, 60, 60);
    pdf.text(label + ':', margin, y);
    pdf.setFont('times', 'bold');
    pdf.text(value, margin + 50, y);
    y += 6;
  });

  y += 10;

  // Alerts Summary
  checkPageBreak(30);
  pdf.setFontSize(14);
  pdf.setFont('times', 'bold');
  pdf.setTextColor(30, 41, 59);
  pdf.text('Compliance Alerts Summary', margin, y);
  y += 8;

  const alertsByType = {
    CRITICAL: data.alerts.filter(a => a.type === 'CRITICAL').length,
    WARNING: data.alerts.filter(a => a.type === 'WARNING').length,
    CAUTION: data.alerts.filter(a => a.type === 'CAUTION').length,
    INFO: data.alerts.filter(a => a.type === 'INFO').length,
  };

  Object.entries(alertsByType).forEach(([type, count]) => {
    checkPageBreak(6);
    pdf.setFontSize(10);
    pdf.setFont('times', 'normal');
    
    if (type === 'CRITICAL') pdf.setTextColor(220, 38, 38);
    else if (type === 'WARNING') pdf.setTextColor(234, 88, 12);
    else if (type === 'CAUTION') pdf.setTextColor(202, 138, 4);
    else pdf.setTextColor(59, 130, 246);
    
    pdf.text(`${type}: ${count}`, margin, y);
    y += 6;
  });

  pdf.setTextColor(0, 0, 0);
  y += 10;

  // Staff with issues
  const staffWithIssues = data.staffMetrics.filter(s => s.penaltyStatus !== 'ok');
  if (staffWithIssues.length > 0) {
    checkPageBreak(30);
    pdf.setFontSize(14);
    pdf.setFont('times', 'bold');
    pdf.setTextColor(30, 41, 59);
    pdf.text('Staff Requiring Attention', margin, y);
    y += 8;

    staffWithIssues.slice(0, 10).forEach(staff => {
      checkPageBreak(6);
      pdf.setFontSize(10);
      pdf.setFont('times', 'normal');
      pdf.setTextColor(60, 60, 60);
      pdf.text(`• ${staff.staffName} - ${staff.manualCorrections} corrections (${staff.penaltyStatus})`, margin, y);
      y += 6;
    });
  }

  // Final footer
  addFooter();

  // Return as blob
  return pdf.output('blob');
};
